import R from 'ramda'
import { encryptObject, decryptObject } from '../core/aes'
import { encrypt as encryptFile } from './file'
import { createData, createFileMetadata } from './format'

/**
 * Create file info
 *
 * @param {{name: String, size: Number, type: String, id: String, path: String, service: String, data: ArrayBuffer, encryptionParams: SymmetricEncryptionParams}} file
 * @return {FileMetadata}
 * @private
 */
export function _createFile (file) {
  return R.converge(
    createFileMetadata,
    [ R.prop('name'), R.prop('size'), R.prop('type'), R.prop('id') ]
  )(file)
}

/**
 * Prepare arguments for multiple file encryptions
 *
 * @param {Base64UrlString} encryptionKey
 * @param {{name: String, size: Number, type: String, id: String, path: String, service: String, data: ArrayBuffer, encryptionParams: SymmetricEncryptionParams}[]} files
 * @return {Array.<Array.<String, String, String, ArrayBuffer, Base64UrlString, SymmetricEncryptionParams>>} 2d array of arguments compatible with encryptFile
 * @private
 */
export function _argumentsForMultipleFileEncryptions (encryptionKey, files) {
  return R.map(
    R.converge(
      R.unapply(R.insert(4, encryptionKey)),
      [ R.prop('id'), R.prop('path'), R.prop('service'), R.prop('data'), R.prop('encryptionParams') ]
    )
  )(files)
}

/**
 * Create data
 *
 * @param {String} text
 * @param {{name: String, size: Number, type: String, id: String, path: String, service: String, data: ArrayBuffer, encryptionParams: SymmetricEncryptionParams}[]} files
 * @return {Data}
 * @private
 */
export function _createData (text, files) {
  return createData(text, R.map(_createFile, files))
}

/**
 * Encrypt data
 *
 * @param {Base64UrlString} encryptionKey
 * @param {SymmetricEncryptionParams} encryptionParams
 * @param {String} text
 * @param {{name: String, size: Number, type: String, id: String, path: String, service: String, data: ArrayBuffer, encryptionParams: SymmetricEncryptionParams}[]} files
 * @return {{data: Base64UrlString, files: Object.<String, ArrayBuffer>}} encrypted data
 * @throws {Error} if a parameter is invalid
 * @private
 */
export function encrypt (encryptionKey, encryptionParams, text, files) {
  return R.useWith(
    R.useWith(
      R.merge,
      [ R.objOf('data'), R.objOf('files') ]
    ),
    [
      R.compose(R.curry(encryptObject)(R.__, encryptionKey, encryptionParams), R.apply(_createData)),
      R.compose(R.map(R.apply(encryptFile)), R.apply(_argumentsForMultipleFileEncryptions))
    ]
  )([text, files], [encryptionKey, files])
}

/**
 * Decrypt data
 *
 * @name decryptData
 * @param {Base64UrlString} data
 * @param {Base64UrlString} decryptionKey
 * @param {SymmetricEncryptionParams} decryptionParams
 * @return {Data} decrypted data
 * @throws {Error} if a parameter is invalid, data is corrupt or wrong key
 * @public
 * @example
 *  const keys = CryptolizeCore.createAsymmetricKeys()
 *  const password = 'password'
 *  const hint = 'hint'
 *  const record = CryptolizeCore.encryptRecord(..., keys.public, ..., [{ password, hint }], ...)
 *  const header = CryptolizeCore.decryptRecord(record, keys.private)[0]
 *  const key = CryptolizeCore.decryptKeyWithSymmetricEncryption(password, header)
 *  const data = CryptolizeCore.decryptData(record.blocks[0].data, key, header.dataEncryptionParams)
 *  console.log(data)
 */
export const decrypt = decryptObject
